import React, { useMemo } from "react";
import { size } from "lodash";
import styled from "styled-components";
import { formatDate } from "@backpacker/date";
import { GlossyThemeProvider, background, prettyScrollbars } from "@glossy";
import logo from "@radarly/assets/logos/logo-text-black.svg?raw";
import Text from "@radarly/components/cards/text";
import { Avatar } from "@radarly/components/identity/avatar";
import Layout from "@radarly/components/insight-pages/layout";
import SubHeader from "@radarly/components/insight-pages/subheader2";
import { useLabelsFromClause } from "@radarly/core/clause/use-cases";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { refineClauseWithoutDate } from "@radarly/models/clause/refiner";
import { getPeriodFromInsightPage } from "@radarly/models/insight-pages/period";
import { PublicPage } from "@radarly/models/public-pages";
import Card from "@radarly/shared/cards";
import SubheaderFilters from "./components/subheader/filters";
import SubheaderPeriod from "./components/subheader/period";

const Body = styled.div`
  top: 0;
  position: absolute;
  background-color: ${background};
  left: 0;
  bottom: 0;
  right: 0;
`;

const Root = styled.div`
  height: 100%;
  overflow: auto;
  ${prettyScrollbars};
`;

const Logo = styled.div`
  width: 128px;
`;

function getPeriodFromSnapshot(snapshot: PublicPage) {
  // const period = getDateFromClause(clause);
  if (!snapshot.customPage.periodSelector) {
    return null;
  }
  return getPeriodFromInsightPage(snapshot.customPage);
}

function useSnapshotCtx(snapshot: PublicPage) {
  return useMemo(() => {
    const clause = snapshot.ctx.filters;
    return {
      nowDate: new Date(snapshot.ctx.generatedOn),
      clause: refineClauseWithoutDate(clause),
      period: getPeriodFromSnapshot(snapshot),
    };
  }, [snapshot]);
}

const RadarlyPublicPagesHome = ({ snapshot }: { snapshot: PublicPage }) => {
  const { nowDate, clause, period } = useSnapshotCtx(snapshot);
  const locale = useCurrentUserLocale();
  const project = useCurrentProject();
  const facets = useFacetsFromContext();
  const { labels, loading } = useLabelsFromClause(
    project.id,
    clause,
    facets,
    locale,
    period
  );

  return (
    <GlossyThemeProvider name="body">
      <Body>
        <Root>
          <GlossyThemeProvider name="surface">
            <SubHeader
              title={snapshot.customPage.title}
              timezone={
                snapshot.customPage.timezone &&
                tr(locale, "CUSTOM_PAGE_TIMEZONE", {
                  timezone: snapshot.customPage.timezone,
                })
              }
              left={
                <Avatar
                  picture={snapshot.customPage.picture}
                  color={snapshot.customPage.color}
                  title={snapshot.customPage.title}
                />
              }
              generated={tr(
                locale,
                "INSIGHT_PAGES::PAGE::SNAPSHOT-GENERATED_ON",
                {
                  date: formatDate(nowDate, locale, "ll"),
                }
              )}
              period={
                !!period && (
                  <SubheaderPeriod
                    period={period}
                    locale={locale}
                    timezone={snapshot.customPage.timezone}
                    nowDate={nowDate}
                  />
                )
              }
              filters={
                !!size(labels) &&
                !loading && <SubheaderFilters labels={labels} locale={locale} />
              }
              right={<Logo dangerouslySetInnerHTML={{ __html: logo }} />}
            />
          </GlossyThemeProvider>
          <Layout
            renderCard={(card, height) => (
              <GlossyThemeProvider name="tile">
                <Card
                  card={card}
                  height={height}
                  timezone={snapshot.customPage.timezone}
                  nowDate={nowDate}
                  readOnly
                />
              </GlossyThemeProvider>
            )}
            renderTitle={(title, type, id) => (
              <Text content={title} isFirst={id === 0} type={type} asHTML />
            )}
            rows={snapshot.customPage.rows}
            locale={locale}
          />
        </Root>
      </Body>
    </GlossyThemeProvider>
  );
};

export default RadarlyPublicPagesHome;
